<script>
import {
  formatConversaListTime,
  truncatePreview,
  formatMessageContent,
} from '@modules/atendimento/utils/message-formatter'
import {
  CONVERSA_STATUS_COLORS,
  CONVERSA_STATUS_LABELS,
  ICONS,
} from '@modules/atendimento/constants/Text'
import { hexToRgb, stringToColor } from '@modules/global/utils/Color'

export default {
  name: 'ConversationListItem',
  props: {
    conversa: {
      type: Object,
      required: true,
    },
    // Conexões WhatsApp do escritório (vivem em data() do host, não no Vuex).
    // Usadas para resolver e exibir o chip do nome da conexão de cada conversa
    // quando há 2+ conexões (gestor que enxerga todas).
    conexoes: {
      type: Array,
      default() {
        return []
      },
    },
    selected: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      icons: ICONS,
    }
  },
  computed: {
    isGroup() {
      return !!(this.conversa.contato && this.conversa.contato.is_group)
    },
    contactName() {
      if (!this.conversa.contato) return 'Desconhecido'
      const c = this.conversa.contato
      // Para grupos: só o subject (campo `nome`). nome_contato/nome_cliente
      // não se aplicam — grupos não vinculam a cliente nem têm contact-book
      // entry. Fallback final é o telefone (JID) pra nunca exibir
      // "Desconhecido" se temos pelo menos o identificador.
      if (c.is_group) {
        return c.nome || c.telefone || 'Grupo'
      }
      // Individual — prioridade:
      //   1) cliente vinculado (nome_cliente)
      //   2) contact book do WhatsApp (nome_contato — o que a conta
      //      conectada salvou pra essa pessoa)
      //   3) profile pushName (nome — o que a própria pessoa colocou)
      //   4) telefone
      return (
        c.nome_cliente ||
        c.nome_contato ||
        c.nome ||
        c.telefone ||
        'Desconhecido'
      )
    },
    whatsappName() {
      if (!this.conversa.contato) return ''
      const c = this.conversa.contato
      if (c.is_group) return ''
      if (!c.nome_cliente) return ''
      const wppName = c.nome_contato || c.nome || ''
      if (wppName && wppName !== c.nome_cliente) return wppName
      return ''
    },
    contactFotoUrl() {
      if (!this.conversa.contato) return ''
      return this.conversa.contato.foto_url || ''
    },
    contactBookName() {
      if (!this.conversa.contato) return ''
      const contato = this.conversa.contato
      if (
        contato.nome_contato &&
        contato.nome &&
        contato.nome_contato !== contato.nome
      ) {
        return contato.nome_contato
      }
      return ''
    },
    fullMessagePreview() {
      return this.conversa.ultima_mensagem_preview || ''
    },
    lastMessagePreview() {
      const preview = this.fullMessagePreview
      return truncatePreview(preview, 50)
    },
    lastMessagePreviewHtml() {
      // Reusa o mesmo formatter da bolha de mensagem (negrito, itálico,
      // escape XSS, etc.). Fonte única da verdade em message-formatter.js.
      return formatMessageContent(this.lastMessagePreview)
    },
    lastMessageTime() {
      return formatConversaListTime(this.conversa.ultima_mensagem_em)
    },
    unreadCount() {
      return this.conversa.mensagens_nao_lidas || 0
    },
    hasUnread() {
      return this.unreadCount > 0
    },
    isLastMessageNote() {
      return !!this.conversa.ultima_mensagem_is_note
    },
    conversaStatus() {
      return this.conversa.status || 'aberta'
    },
    statusColor() {
      return CONVERSA_STATUS_COLORS[this.conversaStatus] || '#9E9E9E'
    },
    statusLabel() {
      return CONVERSA_STATUS_LABELS[this.conversaStatus] || ''
    },
    assignedToName() {
      return this.conversa.assigned_to_nome || ''
    },
    hasAssigned() {
      return !!this.assignedToName
    },
    selectedBgColor() {
      const primary = getComputedStyle(document.documentElement)
        .getPropertyValue('--primary')
        .trim()
      if (!primary) return 'rgba(0, 0, 0, 0.08)'
      const [r, g, b] = hexToRgb(primary)
      return `rgba(${r}, ${g}, ${b}, 0.15)`
    },
    // Conexão da conversa, resolvida pelo id_conexao.
    // Type-safe: compara como string para cobrir o caso em que o localStorage
    // persiste como string (Number|String) e c.id vem como number do backend.
    conexaoAtual() {
      const idConexao = this.conversa && this.conversa.id_conexao
      if (!idConexao) return null
      const list = this.conexoes || []
      return list.find((c) => c && String(c.id) === String(idConexao)) || null
    },
    // Mostra o chip sempre que a conexão da conversa estiver resolvida. O gate
    // em `conexaoAtual` evita exibir um chip "vazio" durante o load assíncrono
    // das conexões — só aparece quando já há nome real para mostrar.
    showConexaoChip() {
      return !!this.conexaoAtual
    },
    conexaoNome() {
      if (!this.conexaoAtual) return ''
      return this.conexaoAtual.nome || 'Conexão #' + this.conexaoAtual.id
    },
    // Cor determinística por string — mesmo padrão do componente client-tags.
    conexaoChipColor() {
      return stringToColor(this.conexaoNome)
    },
  },
}
</script>

<template>
  <div
    data-test="conversation-item"
    class="conversation-list-item d-flex align-center pa-2"
    :style="{
      backgroundColor: selected ? selectedBgColor : '',
      borderLeft: selected
        ? '2px solid var(--primary)'
        : '2px solid transparent',
    }"
    @click="$emit('click', conversa)"
  >
    <!-- Avatar -->
    <div class="avatar-wrapper mr-2" style="position: relative">
      <v-avatar
        size="32"
        :color="isGroup ? 'blue-grey lighten-3' : 'grey lighten-2'"
      >
        <img v-if="contactFotoUrl" :src="contactFotoUrl" :alt="contactName" />
        <icon-svg
          v-else-if="isGroup"
          :name="icons.accountGroup"
          size="18"
          color="white"
          data-test="group-icon"
        />
        <span v-else class="az-text-small">{{ contactName.charAt(0) }}</span>
      </v-avatar>
    </div>

    <div class="flex-grow-1 overflow-hidden">
      <div class="d-flex justify-space-between align-center">
        <div
          class="text-truncate"
          :title="contactName + (whatsappName ? ' (' + whatsappName + ')' : '')"
        >
          <span class="az-text-primary az-size-14 font-medium">
            {{ contactName }}
          </span>
          <span v-if="whatsappName" class="az-text-small ml-1">
            ({{ whatsappName }})
          </span>
        </div>
        <span class="az-text-small az-muted ml-2 flex-shrink-0">
          {{ lastMessageTime }}
        </span>
      </div>
      <div class="preview-row d-flex justify-space-between align-center mt-1">
        <span
          class="az-text-small text-truncate d-inline-flex align-center"
          :class="isLastMessageNote ? 'note-preview' : 'az-muted'"
          :title="fullMessagePreview"
        >
          <icon-svg
            v-if="isLastMessageNote"
            :name="icons.noteIndicator"
            size="10"
            color="amber darken-2"
            class="mr-1"
          />
          <!-- eslint-disable-next-line vue/no-v-html — conteudo passa por formatMessageContent (escape XSS + markdown WhatsApp) -->
          <span class="text-truncate" v-html="lastMessagePreviewHtml" />
        </span>
        <span
          v-if="hasUnread"
          data-test="unread-badge"
          class="unread-badge ml-2 flex-shrink-0"
        >
          {{ unreadCount }}
        </span>
      </div>
      <div v-if="showConexaoChip" class="mt-1">
        <v-chip
          x-small
          :color="$setOpacity(conexaoChipColor, 0.15)"
          :text-color="conexaoChipColor"
          data-test="conexao-chip"
        >
          <icon-svg
            :name="icons.connections"
            size="10"
            :color="conexaoChipColor"
            class="mr-1"
          />
          {{ conexaoNome }}
        </v-chip>
      </div>
      <div
        v-if="hasAssigned"
        data-test="assigned-info"
        class="d-flex align-center mt-1"
      >
        <icon-svg :name="icons.account" size="10" color="grey" class="mr-1" />
        <span class="az-text-small az-muted text-truncate">
          {{ assignedToName }}
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.conversation-list-item
  cursor: pointer
  border-bottom: 1px solid var(--panel-bg-hover)
  transition: background-color 0.15s ease
  background-color: var(--panel-bg)

.conversation-list-item:hover
  background-color: var(--panel-bg-hover)

.avatar-wrapper
  flex-shrink: 0

// Reserva a altura do badge (18px) na linha de preview pra que o item não
// "cresça" quando o contador de não-lidas aparece/some ao marcar como lido.
.preview-row
  min-height: 18px

.unread-badge
  display: inline-flex
  align-items: center
  justify-content: center
  min-width: 18px
  height: 18px
  padding: 0 5px
  border-radius: 9px
  background-color: var(--primary)
  color: var(--text-primary)
  font-size: 10px
  font-weight: 600

.note-preview
  color: #F57F17
  font-style: italic
</style>
